import type { FolderUploadFailureReason } from '@/lib/uploads/folder-upload/types';

import { normalizeLocale, type SupportedLocale } from './config';
import { FOLDER_UPLOAD_PANEL_COPY_EASTERN } from './folder-upload-panel-copy-eastern';
import { FOLDER_UPLOAD_PANEL_COPY_WESTERN } from './folder-upload-panel-copy-western';

/**
 * The upload panel for files and folders uploaded to a workspace folder
 * (#5823). Templates take `{folder}`, `{done}`, `{total}`, `{bytesDone}`,
 * `{bytesTotal}`, `{time}`, `{count}`, `{loaded}`, `{name}` and `{date}`
 * placeholders.
 */
export type FolderUploadPanelCopy = {
  /** Accessible name of the tray that holds every upload. */
  regionLabel: string;
  /** The All/Failed file filter's accessible name. */
  filterLabel: string;
  titleUploading: string;
  titleDone: string;
  titleFailed: string;
  titleCancelled: string;
  /** Cancel all pressed, files still settling. */
  titleCancelling: string;
  titleBlocked: string;
  /** `{done} of {total} files · {bytesDone} of {bytesTotal}` */
  progressSummary: string;
  timeLeft: string;
  summaryUploaded: string;
  summaryFailed: string;
  summaryCancelled: string;
  /** Opens the list of failed files. */
  view: string;
  showFiles: string;
  hideFiles: string;
  cancelAll: string;
  retryFailed: string;
  /** Retry when only some failed files can be retried: "Retry 1". */
  retrySome: string;
  dismiss: string;
  cancelFile: string;
  retryFile: string;
  filterAll: string;
  filterFailed: string;
  statusQueued: string;
  /** `{loaded} of {total}` while a file is sending. */
  statusUploading: string;
  statusRegistering: string;
  statusDone: string;
  statusCancelled: string;
  emptyFoldersFailed: string;
  /** Accessible name of each progress bar. */
  progressLabel: string;
  /** Folder right-click menu item that adds the clipboard's files. */
  pasteMenuItem: string;
  /** Name of a pasted screenshot or image, before its extension. `{date}` */
  pastedImageName: string;
  /** Name of pasted plain text, before `.txt`. `{date}` */
  pastedTextName: string;
  reasons: Record<FolderUploadFailureReason, string>;
};

const COPY: Record<SupportedLocale, FolderUploadPanelCopy> = {
  ...FOLDER_UPLOAD_PANEL_COPY_WESTERN,
  ...FOLDER_UPLOAD_PANEL_COPY_EASTERN,
};

export function getFolderUploadPanelCopy(
  locale?: string | null
): FolderUploadPanelCopy {
  return COPY[normalizeLocale(locale)];
}

/** Fills `{placeholder}` slots; unknown placeholders are left as written. */
export function formatFolderUploadPanelCopy(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match
  );
}
